import { computed } from '@preact/signals';
import { books } from './chapter-store.js';
import { heardChapters, todayKey, chapterKey } from './progress-store.js';
import { currentBible } from './bible-store.js';

/**
 * Daily chapter pick — a deterministic suggestion of "today's reading"
 * for the active bible.
 *
 * Logic:
 *   - Build a flat list of all (book, chapter) pairs in canonical order.
 *   - Seed an index by hashing today's ISO date.
 *   - Walk forward from that index until we find a chapter the user
 *     hasn't heard yet (so the suggestion is fresh). If everything's
 *     been heard, return the seeded chapter unchanged.
 *
 * The pick stays stable for the whole day on the same device. Different
 * devices get the same suggestion because the seed is just the date.
 */

type DailyPick = {
  book: string;
  chapter: number;
  bookName: string;
} | null;

export const dailyPick = computed<DailyPick>(() => {
  const list = books.value;
  if (list.length === 0) return null;
  // Force the computed to recompute when these change.
  void heardChapters.value;
  void currentBible.value;

  const flat: { book: string; chapter: number; bookName: string }[] = [];
  for (const b of list) {
    for (const c of b.chapters) {
      flat.push({ book: b.bookId, chapter: c, bookName: b.bookName });
    }
  }
  if (flat.length === 0) return null;

  const seed = hashDate(todayKey());
  const startIdx = seed % flat.length;
  const bibleId = currentBible.value.bibleId;

  for (let offset = 0; offset < flat.length; offset += 1) {
    const idx = (startIdx + offset) % flat.length;
    const candidate = flat[idx];
    if (!candidate) continue;
    const key = chapterKey(bibleId, candidate.book, candidate.chapter);
    if (!heardChapters.value[key]) return candidate;
  }
  return flat[startIdx] ?? null;
});

function hashDate(iso: string): number {
  // FNV-1a-ish over the ISO date string. Deterministic, small, fine.
  let h = 0x811c9dc5;
  for (let i = 0; i < iso.length; i += 1) {
    h ^= iso.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return Math.abs(h);
}
